
import React from "react";
import {
  Box,
  HStack,
  Spinner,
  Text,
  VStack,
  Link,
} from "@chakra-ui/react";
import {
  ArtifactProto,
  ResponseMessageProto,
  scenarioOutput_ScenarioStatusToJSON,
} from "build-ts/chat_session";
import { ArtifactType } from "build-ts/common";
import ReactMarkdown from "react-markdown";
import { FaTableCellsLarge } from "react-icons/fa6";
import ArtifactsList from "./ArtifactsList";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";

interface MessageProps {
  author: "user" | "ai";
  content: string;
  responseMessage?: ResponseMessageProto | undefined;
  artifacts?: ArtifactProto[];
  langgraphMessageJsonList?: string[];
  onArtifactClick?: (artifact: ArtifactProto) => void;
  onLanggraphMessageClick?: (langgraphMessageJsonList: string[]) => void;
  isLoading: boolean;
  isLastMessage: boolean;
  lsTraceUrl?: string;
  isArtifactsPanelVisible?: boolean;
  allArtifactsForLinks?: ArtifactProto[]; // All artifacts from all messages for dataframe link matching
}

/**
 * Transform [[dataframe: table_name]] → markdown link
 */
const transformDataframeLinks = (text: string): string => {
  const result = text.replace(
    /\[\[dataframe:\s*([^\]]+)\]\]/gi,
    (match, tableName) => {
      const cleanName = tableName.trim();
      return `[${cleanName}](#dataframe:${cleanName})`;
    }
  );
  return result;
};


/**
 * Escapes currency-style dollar signs ($36,756) so remark-math doesn't treat
 * them as LaTeX delimiters. Math expressions always start with \, a letter,
 * or a space after $, never directly with a digit.
 */
const escapeCurrencyDollar = (text: string): string => {
  return text.replace(/\$(?=\d)/g, "\\$");
};

function normalizeLatex(markdown: string): string {
  return markdown.replace(/\\\[/g, "$$").replace(/\\\]/g, "$$").replace(/\\\(/g, "$").replace(/\\\)/g, "$");
}

/**
 * Markdown Renderer
 */
const MarkdownRenderer: React.FC<{
  content: string;
  isArtifactsPanelVisible?: boolean;
  allArtifactsForLinks?: ArtifactProto[];
  onArtifactClick?: (artifact: ArtifactProto) => void;
}> = ({ content, isArtifactsPanelVisible, allArtifactsForLinks, onArtifactClick }) => {
  const baseFontSize = isArtifactsPanelVisible ? "0.875rem" : "1rem";
  const h1Size = isArtifactsPanelVisible ? "1.25rem" : "1.5rem";
  const h2Size = isArtifactsPanelVisible ? "1.125rem" : "1.25rem";
  const h3Size = isArtifactsPanelVisible ? "1rem" : "1.125rem";
  const h4Size = isArtifactsPanelVisible ? "0.875rem" : "1rem";
  const codeFontSize = isArtifactsPanelVisible ? "0.75rem" : "0.875rem";

  // Transform dataframe table to markdown links
  const processedContent = transformDataframeLinks(content);

  // Normalizing the content for LaTeX and escaping currency-style dollar signs
  const normalizedContent = normalizeLatex(escapeCurrencyDollar(processedContent));


  return (
    <Box
      textAlign="left"
      ml={0}
      className="markdown-content"
      w="100%"
      maxW="100%"
      minW="0"
      sx={{
        wordBreak: "break-word",
        overflowWrap: "break-word",
        "& *": { maxWidth: "100%" },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          // Lists
          ul: ({ node, ...props }) => (
            <ul style={{ paddingLeft: "1.25rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol style={{ paddingLeft: "1.25rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
          ),
          li: ({ node, ...props }) => (
            <li style={{ fontSize: baseFontSize, marginBottom: "0.25rem", fontWeight: 500 }} {...props} />
          ),
          // Paragraphs
          p: ({ node, ...props }) => (
            <p style={{ fontSize: baseFontSize, marginTop: "0.5rem", marginBottom: "0.5rem", fontWeight: 500 }} {...props} />
          ),
          strong: ({ node, ...props }) => (
            <strong style={{ fontWeight: 650 }} {...props} />
          ),
          // Headings
          h1: ({ node, ...props }) => (
            <h1 style={{ fontSize: h1Size, fontWeight: "bold", margin: "1rem 0" }} {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h2 style={{ fontSize: h2Size, fontWeight: "bold", margin: "0.875rem 0" }} {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h3 style={{ fontSize: h3Size, fontWeight: "bold", margin: "0.75rem 0" }} {...props} />
          ),
          h4: ({ node, ...props }) => (
            <h4 style={{ fontSize: h4Size, fontWeight: "bold", margin: "0.625rem 0" }} {...props} />
          ),

          /**
           *  CUSTOM LINK RENDERER
           */
          a: ({ href, children }) => {
            if (href?.startsWith("#dataframe:")) {
              const tableName = href.replace("#dataframe:", "");

              // Find the matching artifact from ALL artifacts across all messages
              const matchingArtifact = allArtifactsForLinks?.find(
                (artifact) =>
                  artifact.artifact_type === ArtifactType.KG_DATAFRAME &&
                  artifact.kg_dataframe?.header?.name === tableName
              );

              return (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (matchingArtifact && onArtifactClick) {
                      onArtifactClick(matchingArtifact);
                    }
                  }}
                  style={{
                    color: "#3182ce",
                    cursor: matchingArtifact ? "pointer" : "default",
                    textDecoration: "none",
                    fontWeight: 650,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.25rem",
                  }}
                  onMouseEnter={(e) => {
                    if (matchingArtifact) {
                      e.currentTarget.style.textDecoration = "underline";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = "none";
                  }}
                >
                  <FaTableCellsLarge style={{ display: "inline", flexShrink: 0, fontSize: "0.95em" }} />
                  {children}
                </a>
              );
            }

            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#3182ce",
                  textDecoration: "underline",
                  wordBreak: "break-all",
                }}
              >
                {children}
              </a>
            );
          },

          // Code blocks
          pre: ({ children }) => (
            <pre style={{
              backgroundColor: "bg.surface",
              color: "text.primary",
              padding: "0 0 0 1rem",
              borderRadius: "8px",
              overflowX: "auto",
              margin: "0.75rem 0",
              fontSize: codeFontSize,
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}>
              {children}
            </pre>
          ),
          // Tables
          table: ({ children }) => (
            <div style={{
              overflowX: "auto",
              marginTop: "1rem",
              marginBottom: "1rem",
              maxWidth: "100%",
            }}>
              <table style={{
                borderCollapse: "collapse",
                border: "1px solid #d1d5db",
                minWidth: "100%",
                whiteSpace: "nowrap",
                fontSize: codeFontSize,
              }}>
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead style={{
              backgroundColor: "#f3f4f6",
            }}>
              {children}
            </thead>
          ),
          th: ({ children }) => (
            <th style={{
              border: "1px solid #d1d5db",
              padding: "0.5rem 1rem",
              textAlign: "left",
              fontWeight: 600,
              backgroundColor: "#f3f4f6",
            }}>
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td style={{
              border: "1px solid #d1d5db",
              padding: "0.5rem 1rem",
              textAlign: "left",
            }}>
              {children}
            </td>
          ),
        }}
      >
        {normalizedContent}
      </ReactMarkdown>
    </Box>
  );
};

const MessageComponent: React.FC<MessageProps> = ({
  author,
  content,
  responseMessage,
  artifacts,
  langgraphMessageJsonList,
  onArtifactClick,
  onLanggraphMessageClick,
  isLoading,
  isLastMessage,
  lsTraceUrl,
  isArtifactsPanelVisible,
  allArtifactsForLinks,
}) => {
  return (
    <HStack
      alignSelf={author === "user" ? "flex-end" : "flex-start"}
      w={author === "user" ? "auto" : "100%"}
    >
      <Box
        bg={author === "user" ? "purple.50" : "white"}
        px={3}
        py={2}
        borderRadius="lg"
        maxW="100%"
        flex="1"
        ml={author === "user" ? "3" : "0"}
        mb={3}
      >
        {isLoading && isLastMessage ? (
          // Loading state
          <VStack align="start" spacing={2}>
            <HStack w="100%" justify="space-between">
              <Box display="flex" alignItems="center" minH="24px">
                <Spinner size="sm" color="purple.700" mr={2} />
                <Text fontSize={isArtifactsPanelVisible ? "sm" : "md"} color="gray.500" mb={0}>
                  Thinking ...
                </Text>
              </Box>
            </HStack>
          </VStack>
        ) : (
          // Normal message state
          <VStack align="start" spacing={0} w="100%">
            {author === "ai" && (
              <VStack w="100%">
                <HStack w="100%" justify="flex-end">
                  {/* LangSmith trace link for Eva */}
                  {lsTraceUrl && (
                    <Link
                      href={lsTraceUrl}
                      isExternal
                      fontSize={isArtifactsPanelVisible ? "xs" : "sm"}
                      color="purple.600"
                      fontWeight="bold"
                      _hover={{ color: "purple.700", textDecoration: "none" }}
                    >
                      Trace
                    </Link>
                  )}
                </HStack>
              </VStack>
            )}
            {author === "ai" && responseMessage?.scenario_output && (
              <VStack align="start" spacing={2} w="100%" mt={2}>
                {(artifacts && artifacts.length > 0) || (langgraphMessageJsonList && langgraphMessageJsonList.length > 0) ? (
                  <Box w="100%">
                    <ArtifactsList
                      artifacts={artifacts}
                      langgraphMessageJsonList={langgraphMessageJsonList}
                      onArtifactClick={onArtifactClick}
                      onLanggraphMessageClick={onLanggraphMessageClick}
                    />
                  </Box>
                ) : null}
                <Box textAlign="left">
                  <Text fontSize={isArtifactsPanelVisible ? "sm" : "md"} fontWeight="bold" mb={1}>
                    Status: {responseMessage.scenario_output.status !== undefined
                      ? scenarioOutput_ScenarioStatusToJSON(responseMessage.scenario_output.status)
                      : "N/A"}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize={isArtifactsPanelVisible ? "sm" : "md"} fontWeight="bold" mb={1} textAlign="left">
                    Findings:
                  </Text>
                  <Text fontSize={isArtifactsPanelVisible ? "sm" : "md"} m={0} textAlign="left">
                    <MarkdownRenderer
                      content={responseMessage.scenario_output.findings || "N/A"}
                      isArtifactsPanelVisible={isArtifactsPanelVisible}
                      allArtifactsForLinks={allArtifactsForLinks}
                      onArtifactClick={onArtifactClick}
                    />
                  </Text>
                </Box>
              </VStack>
            )}
            {author === "ai" && responseMessage?.scenario_synthesis_output && (
              <VStack align="start" spacing={2} w="100%" mt={2}>
                {(artifacts && artifacts.length > 0) || (langgraphMessageJsonList && langgraphMessageJsonList.length > 0) ? (
                  <Box w="100%">
                    <ArtifactsList
                      artifacts={artifacts}
                      langgraphMessageJsonList={langgraphMessageJsonList}
                      onArtifactClick={onArtifactClick}
                      onLanggraphMessageClick={onLanggraphMessageClick}
                    />
                  </Box>
                ) : null}
                <Box>
                  <Text fontSize={isArtifactsPanelVisible ? "md" : "sm"} fontWeight="bold" mb={1} textAlign="left">
                    Output String:
                  </Text>
                  <Text fontSize={isArtifactsPanelVisible ? "md" : "sm"} m={0} textAlign="left">
                    <MarkdownRenderer
                      content={responseMessage.scenario_synthesis_output.output_string || "N/A"}
                      isArtifactsPanelVisible={isArtifactsPanelVisible}
                      allArtifactsForLinks={allArtifactsForLinks}
                      onArtifactClick={onArtifactClick}
                    />
                  </Text>
                </Box>
                <Box>
                  <Text fontSize={isArtifactsPanelVisible ? "md" : "sm"} fontWeight="bold" mb={1} textAlign="left">
                    Notify:
                  </Text>
                  <Text fontSize={isArtifactsPanelVisible ? "md" : "sm"} m={0} textAlign="left">
                    {responseMessage.scenario_synthesis_output.notify ? "Yes" : "No"}
                  </Text>
                </Box>
              </VStack>
            )}
            {author === "ai" && !responseMessage?.scenario_output && !responseMessage?.scenario_synthesis_output && (
              <VStack align="start" spacing={1} w="100%">
                {(artifacts && artifacts.length > 0) || (langgraphMessageJsonList && langgraphMessageJsonList.length > 0) ? (
                  <Box w="100%">
                    <ArtifactsList
                      artifacts={artifacts}
                      langgraphMessageJsonList={langgraphMessageJsonList}
                      onArtifactClick={onArtifactClick}
                      onLanggraphMessageClick={onLanggraphMessageClick}
                    />
                  </Box>
                ) : null}
                <Box w="100%" mx={isArtifactsPanelVisible ? 1 : 2}>
                  <MarkdownRenderer
                    content={content}
                    isArtifactsPanelVisible={isArtifactsPanelVisible}
                    allArtifactsForLinks={allArtifactsForLinks}
                    onArtifactClick={onArtifactClick}
                  />
                </Box>
              </VStack>
            )}
            {author === "user" && (
              <Text mb={0} alignSelf="flex-end" fontSize={isArtifactsPanelVisible ? "sm" : "md"} textAlign="left" fontWeight={500}>
                {content}
              </Text>
            )}
          </VStack>
        )}
      </Box>
    </HStack>
  );
};

export default MessageComponent;